import React, { useEffect, useState } from "react";
import "./MortgageDetailsTable.style.css";
import { humanReadableDate } from "../../app/utils";

const MRA_APPLICATIONS_FETCH_URL = "/api/all_mra_applications";
const MRA_ARCHIVE_APPLICATION_FETCH_URL = "/api/all_mra_archive_applications";

const CompanyCell = ({
  name,
  parent_name,
  trademark,
  licence_type,
  contact,
  address,
  phone,
  email
}) => {
  let contactTitle = "Compliance Officer";

  if (licence_type === "Mortgage Brokerage") {
    contactTitle = "Principal Broker";
  }

  return (
    <>
      {parent_name && (
        <div>
          <span style={{ fontWeight: "bold" }}>{parent_name}</span>
          <span>, operating as </span>
          <span style={{ fontWeight: "bold" }}>{name}</span>
        </div>
      )}
      {!parent_name && trademark && (
        <div>
          <span style={{ fontWeight: "bold" }}>{name}</span>
          <span> operating as </span>
          <span style={{ fontWeight: "bold" }}>{trademark}</span>
        </div>
      )}
      {!parent_name && !trademark && <div style={{ fontWeight: "bold" }}>{name}</div>}
      <div>{`${contactTitle}: ${contact}`}</div>
      <div>{address}</div>
      <div>&nbsp;</div>
      <div>{phone}</div>
      <div>{email}</div>
    </>
  );
};

const columns = [
  {
    id: "agencyName",
    label: "Company information",
    align: "left",
    minWidth: 320
  },
  {
    id: "licenceNumber",
    label: "Licence number",
    align: "center",
    minWidth: 140
  },
  {
    id: "licenceSubType",
    label: "Licence type",
    minWidth: 140,
    align: "center"
  },
  {
    id: "originalIssuedDate",
    label: "Original Issued date",
    minWidth: 164,
    align: "center"
  },
  {
    id: "endDate",
    label: "Expiry date",
    minWidth: 164,
    align: "center"
  },
  {
    id: "licenceStatus",
    label: "Licence status",
    minWidth: 140,
    align: "center"
  },
  {
    id: "licenceStatusChangeDate",
    label: "Licence status change date",
    minWidth: 164,
    align: "center"
  },
  {
    id: "administrativePenaltiesNote",
    label: "Comments",
    minWidth: 300,
    align: "center"
  }
];

export default function MortgageDetailsTable() {
  const [applications, setApplications] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState("loading");

  useEffect(() => {
    const fetchPromises = [];
    fetchPromises.push(fetch(MRA_APPLICATIONS_FETCH_URL).then((resp) => resp.json()));
    fetchPromises.push(fetch(MRA_ARCHIVE_APPLICATION_FETCH_URL).then((resp) => resp.json()));

    Promise.all(fetchPromises)
      .then((responses) => {
        if (responses && Array.isArray(responses) && responses.length === 2) {
          const apps = responses[0];
          const archiveApps = responses[1];
          let applications = [];

          if (apps && apps["applicationsList"]) {
            applications = [...apps["applicationsList"]];
          }

          if (archiveApps && archiveApps["applicationsList"]) {
            applications = [...applications, ...archiveApps["applicationsList"]];
          }

          setLoadingStatus(null);
          const cmpFunc = (app1, app2) =>
            app1.agencyName?.toLowerCase().localeCompare(app2.agencyName?.toLowerCase());
          setApplications(applications.sort(cmpFunc));
        } else {
          setLoadingStatus("error");
        }
      })
      .catch((err) => setLoadingStatus("error"));
  }, []);

  if (loadingStatus === "loading") {
    return <div className="status loading">Loading...</div>;
  } else if (loadingStatus === "error") {
    return (
      <div className="status error">
        <div>Error loading data.</div>
        <div>Please try again later.</div>
      </div>
    );
  }

  return (
    <>
      <div className="expiryMsgNote">
        All mortgage licenses expire on October 31<sup>st</sup> each year.
      </div>
      <div className="mraDetailsTable">
        <table>
          <tbody>
            <tr>
              {columns.map((column) => {
                return (
                  <th key={column.id} className="tableHeader" style={{ minWidth: column.minWidth, width: "100%" }}>
                    {column.label}
                  </th>
                );
              })}
            </tr>
            {applications.map((row) => (
              <tr className="tableRow" key={row["licenceNumber"]}>
                {columns.map((column) => {
                  if (column.id === "agencyName") {
                    return (
                      <td className="tableCell" key={`${column.id}-${row["licenceNumber"]}`}>
                        <CompanyCell
                          name={row[column.id]}
                          parent_name={row["parentAgencyName"]}
                          trademark={row["companyTrademark"]}
                          licence_type={row["licenceSubType"]}
                          contact={row["contactName"]}
                          address={row["address"] ?? ""}
                          phone={row["phoneNumber"] ?? ""}
                          email={row["emailAddress"] ?? ""}
                        />
                      </td>
                    );
                  }
                  if (column.id === "licenceStatusChangeDate") {
                    const date = row[column.id];
                    //Do not show status change date if licence is Active or expired
                    if (row["licenceStatus"] === "Active" || row["licenceStatus"] === "Expired") {
                      return <td />;
                    }
                    return (
                      <td className="tableCell" key={`${column.id}-${row["licenceNumber"]}`}>
                        {date ? humanReadableDate(date) : ""}
                      </td>
                    );
                  }
                  else if(column.id === "originalIssuedDate") {
                    const date = row[column.id];
                    return (
                      <td className="tableCell" key={`${column.id}-${row["licenceNumber"]}`}>
                        {date ? humanReadableDate(date) : ""}
                      </td>
                    );
                  }
                  else if(column.id === "endDate") {
                    if (!(row["licenceStatus"] === "Active" || row["licenceStatus"] === "Expired")) {
                      return <td />;
                    }
                    const date = row[column.id];
                    return (
                      <td className="tableCell" key={`${column.id}-${row["licenceNumber"]}`}>
                        {date ? humanReadableDate(date) : ""}
                      </td>
                    );
                  }
                  return (
                    <td className="tableCell" key={`${column.id}-${row["licenceNumber"]}`}>
                      {row[column.id]}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
